<div class="mt-3">
  <a routerLink="/capture-admin/tools/import-ars-consult-notes/history">&lt;&lt; Back to list</a>
</div>

@if (loading) {
  <section class="mt-3"><mat-spinner diameter="30" /></section>
} @else {
  <h5 class="mt-3 phi">{{ arsConsultNoteImport.documentName }}</h5>
  @if (arsConsultNoteImport.patientAttachmentId) {
    <div>
      <small>
        Consult Note:
        <a
          target="_blank"
          routerLink="/capture-admin/consult-notes/{{ arsConsultNoteImport.patientAttachmentId }}">
          {{ arsConsultNoteImport.patientAttachmentId }}
        </a>
      </small>
    </div>
  }

  <div class="d-flex flex-wrap justify-content-between gap-4 my-3">
    <div>
      <strong class="text-uppercase">Client:</strong>
      <div>{{ arsConsultNoteImport.client?.name || '--' }}</div>
    </div>
    <div>
      <strong class="text-uppercase">Patient:</strong>
      <app-patient-name [patient]="arsConsultNoteImport.patient" [canViewPatient]="true" [showAlternateNames]="false" />
    </div>
    <div>
      <strong class="text-uppercase">Prescriber:</strong>
      <app-prescriber-name [provider]="arsConsultNoteImport.provider" />
    </div>
    <div>
      <strong class="text-uppercase">Specialties:</strong>
      <div><app-specialty-badges [specialties]="arsConsultNoteImport.provider?.specialties" [showBlank]="true" /></div>
    </div>
    <div>
      <strong class="text-uppercase">Status:</strong>
      <div>{{ arsConsultNoteImport.status | replace: '_' : ' ' | titlecase }}</div>
    </div>
    <div>
      <strong class="text-uppercase">Imported:</strong>
      <div>{{ arsConsultNoteImport.createdAt | date: 'M/d/yyyy h:mm a' }}</div>
    </div>
  </div>

  <div class="row">
    <div class="col-8">
      <div class="row g-0">
        <ul class="col nav nav-tabs bg-light ps-1 pt-1">
          <li class="nav-item">
            <button
              type="button"
              class="nav-link"
              [class.active]="activeTab === 'consultNote'"
              (click)="activeTab = 'consultNote'">
              Consult Note
            </button>
          </li>
          <li class="nav-item">
            <button type="button" class="nav-link" [class.active]="activeTab === 'ocr'" (click)="activeTab = 'ocr'">
              OCR
            </button>
          </li>
        </ul>
      </div>

      <!-- hidden rather than removed so the PDF isn't reloaded when switching tabs -->
      <div [hidden]="activeTab !== 'consultNote'">
        @if (pdfFile) {
          <!-- not editable since annotations are saved by file type, which imports don't have -->
          <app-pdf-basic-viewer [pdfFile]="pdfFile" [height]="'80vh'" [editable]="false" />
        } @else {
          <p class="mt-2">No document is attached to this import.</p>
        }
      </div>

      <div class="ocr-content" [hidden]="activeTab !== 'ocr'">
        @if (arsConsultNoteImport.content) {
          <div class="card m-1">
            <div class="card-title m-0 pt-1 px-2">
              <h6 class="pt-1">Consult Note Text (OCR)</h6>
            </div>
            <div class="card-body p-1">
              <pre class="phi">{{ arsConsultNoteImport.content }}</pre>
            </div>
          </div>
        } @else {
          <p class="mt-2">No OCR text is available for this import.</p>
        }
      </div>
    </div>
    <div class="col-4">
      @if (arsConsultNoteImport.canRestartOcr || arsConsultNoteImport.canRestartLlm) {
        <div class="mb-3">
          <button
            type="button"
            class="btn btn-danger"
            [disabled]="restarting"
            (click)="onRestartClick(arsConsultNoteImport.canRestartOcr ? 'ocr' : 'llm')">
            {{ arsConsultNoteImport.canRestartOcr ? 'Restart OCR' : 'Restart LLM' }}
          </button>
          @if (restartError) {
            <div class="text-danger small mt-2">{{ restartError }}</div>
          }
        </div>
      }
      @if (restartedStep) {
        <div class="alert alert-success small">
          {{ restartedStep === 'ocr' ? 'OCR' : 'LLM' }} processing was queued again.
        </div>
      }
      <div class="card">
        <div class="card-header"><strong>Assign Office</strong></div>
        <div class="card-body">
          <div class="mb-3">
            <div class="mb-1 text-uppercase"><strong>Office Text Extracted From Consult Note</strong></div>
            @if (llmResponse; as extracted) {
              <!-- mirrors the app-office layout; values are shown as the LLM returned them -->
              <div class="small phi">
                @if (extracted.officeName) {
                  <div class="fst-italic">{{ extracted.officeName }}</div>
                }
                @if (extracted.officeAddress) {
                  <div>{{ extracted.officeAddress }}</div>
                }
                @if (extracted.officePhone || extracted.officeFax) {
                  <div>
                    @if (extracted.officePhone) {
                      <strong>P:</strong> {{ extracted.officePhone }}
                    }
                    @if (extracted.officeFax) {
                      <strong>F:</strong> {{ extracted.officeFax }}
                    }
                  </div>
                }
              </div>
            } @else {
              <div class="small text-muted">No office text was extracted.</div>
            }
          </div>
          @if (arsConsultNoteImport.requiresIntervention) {
            <div class="alert alert-danger small">
              <div class="text-uppercase"><strong>Requires Intervention</strong></div>
              <div class="text-break">{{ arsConsultNoteImport.exception }}</div>
            </div>
          }
          @if (arsConsultNoteImport.office) {
            <div class="mb-1 text-uppercase"><strong>Assigned Office</strong></div>
            <app-office [office]="arsConsultNoteImport.office" />
            @if (noteCreatedOnAssign) {
              <div class="alert alert-success small mt-3 mb-0">The consult note was created.</div>
            }
          } @else if (arsConsultNoteImport.canAssignOffice) {
            <form (ngSubmit)="onAssignOfficeSubmit()">
              <label class="form-label text-uppercase" for="assignOfficeSelect">
                <strong>Assign an Office to this Consult Note</strong>
              </label>
              <app-office-fax-select [(office)]="selectedOffice" [labelForId]="'assignOfficeSelect'" />
              @if (assignOfficeError) {
                <div class="text-danger small mt-2">{{ assignOfficeError }}</div>
              }
              <button type="submit" class="btn btn-primary mt-3" [disabled]="!selectedOffice || assigningOffice">
                Assign
              </button>
            </form>
          } @else {
            <div class="small text-muted">
              An office can only be assigned when resolving details stops and the import requires intervention.
            </div>
          }
        </div>
      </div>
    </div>
  </div>
}
